import type { AuditMetadata } from "@salon/types/audit";
import type { AuditEntry } from "@/gql/graphql";

/**
 * Format audit entry description using metadata when available
 */
export function formatAuditDescription(entry: AuditEntry): string {
  const metadata = entry.metadata as AuditMetadata | null;
  const userName = entry.user?.fullName || "System";

  if (!metadata) {
    // Fallback to generic description
    return `${userName} ${getOperationVerb(entry.operation)} ${entry.tableName}`;
  }

  // Generate rich descriptions based on metadata
  switch (entry.tableName) {
    case "appointment":
      return formatAppointmentDescription(entry, metadata, userName);
    case "appointmentService":
      return formatAppointmentServiceDescription(entry, metadata, userName);
    case "customer":
      return formatCustomerDescription(entry, metadata, userName);
    default:
      return `${userName} ${getOperationVerb(entry.operation)} ${entry.tableName}`;
  }
}

function formatAppointmentDescription(
  entry: AuditEntry,
  metadata: AuditMetadata,
  _userName: string
): string {
  const customerName = metadata.customerName;

  switch (entry.operation) {
    case "INSERT":
      return `Created appointment${customerName ? ` for ${customerName}` : ""}`;
    case "UPDATE":
      return `Updated appointment${customerName ? ` for ${customerName}` : ""}`;
    case "DELETE":
      return `Cancelled appointment${customerName ? ` for ${customerName}` : ""}`;
    default:
      return `Modified appointment${customerName ? ` for ${customerName}` : ""}`;
  }
}

function formatAppointmentServiceDescription(
  entry: AuditEntry,
  _metadata: AuditMetadata,
  userName: string
): string {
  // For appointmentService, fallback to generic description since we don't have specific metadata
  return `${userName} ${getOperationVerb(entry.operation)} service assignment`;
}

function formatCustomerDescription(
  entry: AuditEntry,
  _metadata: AuditMetadata,
  userName: string
): string {
  // For customer, fallback to generic description since we don't have specific metadata
  return `${userName} ${getOperationVerb(entry.operation)} customer profile`;
}

function getOperationVerb(operation: string): string {
  switch (operation) {
    case "INSERT":
      return "created";
    case "UPDATE":
      return "updated";
    case "DELETE":
      return "deleted";
    default:
      return "modified";
  }
}

/**
 * Get contextual subtitle from metadata
 */
export function getMetadataSubtitle(entry: AuditEntry): string | null {
  const metadata = entry.metadata as AuditMetadata | null;

  if (!metadata) return null;

  const parts: string[] = [];

  if (metadata.customerName) {
    parts.push(`Customer: ${metadata.customerName}`);
  }

  return parts.length > 0 ? parts.join(" • ") : null;
}
